import React from 'react';
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    FormHelperText,
    TextareaAutosize,
    Button,
    CircularProgress,
    Paper,
    Fade,
    TextField,
    Divider
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AIIcon from '../../../assets/svgs/AI-Icon.svg';


const ConfigurationTab = ({
    aiSettings,
    setAiSettings,
    validationError,
    setValidationError,
    aiUnderstanding,
    setAiUnderstanding,
    verifying,
    handleVerify,
    testPrompt,
    setTestPrompt,
    testPromptLoading,
    testPromptResult,
    handleTestPrompt,
    question
}) => {
    return (
        <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '16px',
            height: '100%',
            overflow: 'auto',
            paddingTop: '8px'
        }}>
            {/* AI Field Description */}
            <FormControl fullWidth>
                <InputLabel
                    shrink
                    sx={{
                        fontSize: '16px',
                        fontWeight: 600,
                        color: validationError ? 'error.main' : 'text.primary',
                        '&.Mui-focused': {
                            color: validationError ? 'error.main' : 'primary.main'
                        }
                    }}
                >
                    AI Field Description
                </InputLabel>
                <TextareaAutosize
                    minRows={3}
                    maxRows={6}
                    placeholder="Describe what this field represents to help AI understand the context. Be specific about the type of data, format, and any special requirements."
                    value={aiSettings.aiDescription}
                    onChange={(e) => {
                        setAiSettings(prev => ({
                            ...prev,
                            aiDescription: e.target.value
                        }));
                        if (validationError) setValidationError(null);
                        if (aiUnderstanding) setAiUnderstanding(null);
                    }}
                    style={{
                        width: '100%',
                        minHeight: '90px',
                        padding: '16px',
                        fontSize: '14px',
                        lineHeight: '1.6',
                        fontFamily: 'inherit',
                        border: validationError ? '2px solid #d32f2f' : '2px solid #e0e0e0',
                        borderRadius: '8px',
                        resize: 'vertical',
                        outline: 'none',
                        marginTop: '24px',
                        backgroundColor: '#fafafa',
                        transition: 'all 0.2s ease',
                        '&:focus': {
                            borderColor: validationError ? '#d32f2f' : '#1976d2',
                            backgroundColor: 'white'
                        }
                    }}
                    onFocus={(e) => {
                        e.target.style.borderColor = validationError ? '#d32f2f' : '#1976d2';
                        e.target.style.backgroundColor = 'white';
                    }}
                    onBlur={(e) => {
                        e.target.style.borderColor = validationError ? '#d32f2f' : '#e0e0e0';
                        e.target.style.backgroundColor = '#fafafa';
                    }}
                />
                <FormHelperText
                    error={!!validationError}
                    sx={{ fontSize: '13px', marginTop: '8px', marginLeft: '0px' }}
                >
                    {validationError || "Provide a detailed description to help AI understand what kind of information should go in this field."}
                </FormHelperText>
            </FormControl>

            {/* Verify Button */}
            <Box sx={{ 
                display: 'flex', 
                gap: '12px', 
                justifyContent: 'flex-end',
                marginTop: '8px',
                marginBottom: '8px'
            }}>
                <Button
                    variant="contained"
                    onClick={handleVerify}
                    disabled={verifying || !aiSettings.aiDescription.trim()}
                    startIcon={verifying ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                    sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        minWidth: '120px'
                    }}
                >
                    {verifying ? 'Verifying...' : 'Verify'}
                </Button>
            </Box>

            {/* AI Understanding Display */}
            {aiUnderstanding && (
                <Fade in={true}>
                    <Paper sx={{
                        padding: '20px',
                        backgroundColor: '#e8f5e8',
                        border: '2px solid #4caf50',
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(76, 175, 80, 0.15)'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                            <CheckCircleIcon sx={{ color: '#2e7d32' }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                                AI Understanding
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{
                            color: '#2e7d32',
                            lineHeight: 1.6,
                            whiteSpace: 'pre-line',
                            fontFamily: 'inherit'
                        }}>
                            {aiUnderstanding}
                        </Typography>
                    </Paper>
                </Fade>
            )}

            {/* AI Test Prompt Section */}
            <Divider sx={{ margin: '24px 0 16px 0' }} />
            
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                marginBottom: '16px' 
            }}>
                <AIIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                    AI Test Prompt
                </Typography>
            </Box>

            <Typography variant="body2" sx={{ 
                color: '#6c757d', 
                marginBottom: '16px',
                lineHeight: 1.6
            }}>
                Test how AI will respond to this field with a specific prompt. Enter sample data to see how the AI will process it.
            </Typography>

            <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                placeholder="Enter test data (e.g., 'Patient has diabetes and high blood pressure')"
                value={testPrompt}
                onChange={(e) => setTestPrompt(e.target.value)}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fafafa',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#1976d2'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#1976d2',
                            borderWidth: '2px'
                        }
                    }
                }}
            />

            <Box sx={{ 
                display: 'flex', 
                gap: '12px', 
                justifyContent: 'flex-end',
                marginTop: '8px'
            }}>
                <Button
                    variant="contained"
                    onClick={handleTestPrompt}
                    disabled={testPromptLoading || !testPrompt.trim() || !aiSettings.aiDescription.trim()}
                    startIcon={testPromptLoading ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                    sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        minWidth: '140px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)'
                        }
                    }}
                >
                    {testPromptLoading ? 'Testing...' : 'AI Test Prompt'}
                </Button>
            </Box>

            {/* Test Result Display */}
            {testPromptResult && (
                <Fade in={true}>
                    <Paper sx={{
                        padding: '20px',
                        backgroundColor: '#e3f2fd',
                        border: '2px solid #2196f3',
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(33, 150, 243, 0.15)',
                        marginTop: '16px'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                            <AIIcon sx={{ color: '#1976d2' }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                                Test Result
                            </Typography>
                        </Box>
                        
                        <Box sx={{ marginBottom: '16px' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', marginBottom: '4px' }}>
                                Test Data:
                            </Typography>
                            <Typography variant="body2" sx={{ 
                                color: '#1565c0',
                                fontStyle: 'italic',
                                backgroundColor: '#f5f5f5',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                border: '1px solid #e0e0e0'
                            }}>
                                "{testPromptResult.testData}"
                            </Typography>
                        </Box>

                        <Box sx={{ marginBottom: '16px' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', marginBottom: '4px' }}>
                                AI Response:
                            </Typography>
                            <Typography variant="body2" sx={{ 
                                color: '#1565c0',
                                backgroundColor: '#f5f5f5',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                border: '1px solid #e0e0e0',
                                whiteSpace: 'pre-line'
                            }}>
                                {testPromptResult.aiResponse?.suggestions ? 
                                    Object.entries(testPromptResult.aiResponse.suggestions).map(([key, value]) => {
                                        // Handle different value types
                                        let displayValue = value;
                                        if (typeof value === 'object' && value !== null) {
                                            if (Array.isArray(value)) {
                                                displayValue = value.join(', ');
                                            } else {
                                                displayValue = JSON.stringify(value, null, 2);
                                            }
                                        }
                                        return `${key}: ${displayValue}`;
                                    }).join('\n') : 
                                    'No suggestions generated'
                                }
                            </Typography>
                        </Box>

                        {testPromptResult.aiResponse?.summary && (
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', marginBottom: '4px' }}>
                                    Summary:
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                    color: '#1565c0',
                                    fontStyle: 'italic'
                                }}>
                                    {testPromptResult.aiResponse.summary}
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                </Fade>
            )}
        </Box>
    );
};

export default ConfigurationTab;